import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  FogbugzBugBarResult,
  FogbugzFormulatorCodeFinding,
  FogbugzFormulatorCoverage,
  FogbugzFormulatorDuplicate,
  FogbugzFormulatorEvtxSummary,
  FogbugzFormulatorSessionView,
  FogbugzFormulatorSlots,
  FogbugzFormulatorStartBody,
  FogbugzFormulatorSubmitMetadata,
  FogbugzFormulatorSubmitResult,
  FogbugzFormulatorToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { api } from "@/lib/api/client";

/**
 * Data hooks for the SpecTree Toolbox FogBugz Formulator page (epic
 * PI-SPEC-SW-E309, feature PI-SPEC-SW-1620). Every request goes to
 * `/api/v1/fogbugz/toolbox-interview/*` through the shared API client (auth
 * refresh, `ApiError` with the server's code); nothing here calls `fetch`.
 *
 * The session is server state: SpecTree stores the interview text (routing,
 * transcript, slots, coverage) in the database with a sliding two-hour idle
 * lifetime, so a reload or an app restart reads it back. Screenshots and
 * `.evtx` files never reach SpecTree before submit: they stay in the
 * Toolbox's private session folder, listed with
 * {@link useToolboxInterviewAttachments} (a bridge read, not a request).
 */

/** The route prefix, under the API client's `/api/v1`. */
export const FOGBUGZ_TOOLBOX_INTERVIEW_BASE = "/fogbugz/toolbox-interview";

/**
 * Query key factory. `session` is the one a finished turn and every mutation
 * invalidates; `active` is the resume list.
 */
export const fogbugzToolboxInterviewKeys = {
  all: ["fogbugz-toolbox-interview"] as const,
  active: () => [...fogbugzToolboxInterviewKeys.all, "active"] as const,
  session: (id: string) => [...fogbugzToolboxInterviewKeys.all, "session", id] as const,
  review: (id: string) => [...fogbugzToolboxInterviewKeys.all, "review", id] as const,
  duplicates: (id: string) => [...fogbugzToolboxInterviewKeys.all, "duplicates", id] as const,
  attachments: (id: string) => [...fogbugzToolboxInterviewKeys.all, "attachments", id] as const,
};

/** Where a session is: still being written, being filed, or filed as a case. */
export type ToolboxInterviewStatus = "active" | "submitting" | "filed";

/** One message of the interview, as SpecTree stores it. */
export interface ToolboxTranscriptMessage {
  role: "assistant" | "user";
  content: string;
}

/**
 * A session as `GET /:id` returns it: the contract's session view plus the
 * fields this page reads (the transcript, what Claude found in the code,
 * whether it was filed).
 */
export type ToolboxInterviewSession = FogbugzFormulatorSessionView & {
  areaPath?: string | null;
  status: ToolboxInterviewStatus;
  filedCaseId: number | null;
  slots?: FogbugzFormulatorSlots;
  transcript: ToolboxTranscriptMessage[];
  codeFindings: FogbugzFormulatorCodeFinding[];
  turns: number;
  /** True when the person holds a non-Viewer role on the linked ADO project. */
  callerHasRole?: boolean;
  /** True when grooming is set up (repositories mapped) on the linked ADO project. */
  codeSetUp?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

/** `GET /:id/review` and `PUT /:id/intent`: the intent.md as it would be filed, its coverage and the Bug bar. */
export interface ToolboxInterviewReview {
  intentMarkdown: string;
  bugBar: FogbugzBugBarResult;
  coverage: FogbugzFormulatorCoverage;
  version: number;
}

/** `POST /:id/dedupe`: possible duplicate open cases. `unavailable` means the search failed (unknown, not none). */
export interface ToolboxInterviewDuplicates {
  duplicates: FogbugzFormulatorDuplicate[];
  unavailable: boolean;
}

/** `POST /evtx-digest`: the deterministic digest of one `.evtx` file (no AI, nothing stored). */
export interface ToolboxEvtxDigest {
  fileName: string;
  bytes: number;
  summaryFileName: string;
  digestMarkdown: string;
  truncated: boolean;
  digestStats: { recordsSeen: number; recordsParsed: number };
}

interface DataResponse<T> {
  data: T;
}

/** One file of the multipart submit. */
export interface ToolboxSubmitFile {
  name: string;
  bytes: Uint8Array;
}

export interface SubmitToolboxInterviewInput {
  sessionId: string;
  metadata: FogbugzFormulatorSubmitMetadata;
  screenshots: readonly ToolboxSubmitFile[];
  eventLogs: readonly ToolboxSubmitFile[];
}

/** Multipart field names of `POST /:id/submit` and `POST /evtx-digest` (the API's own names). */
export const TOOLBOX_SUBMIT_FIELDS = {
  metadata: "metadata",
  screenshot: "screenshot",
  evtx: "evtx",
  digestFile: "file",
} as const;

/** A `Blob` of `bytes`, copied so a shared or detached buffer from the bridge never leaks into the request. */
function blobOf(bytes: Uint8Array): Blob {
  return new Blob([new Uint8Array(bytes)]);
}

/**
 * The multipart body of `POST /:id/submit`: the JSON `metadata` field first,
 * then one `screenshot` part per screenshot and one `evtx` part per event
 * log, each with its file name. Pure; exported for tests.
 */
export function submitFormData(input: Omit<SubmitToolboxInterviewInput, "sessionId">): FormData {
  const form = new FormData();
  form.append(TOOLBOX_SUBMIT_FIELDS.metadata, JSON.stringify(input.metadata));
  for (const file of input.screenshots) form.append(TOOLBOX_SUBMIT_FIELDS.screenshot, blobOf(file.bytes), file.name);
  for (const file of input.eventLogs) form.append(TOOLBOX_SUBMIT_FIELDS.evtx, blobOf(file.bytes), file.name);
  return form;
}

/** The caller's resumable sessions (active, unexpired), newest first. */
export function useActiveToolboxInterviews(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: fogbugzToolboxInterviewKeys.active(),
    queryFn: () => api.get<DataResponse<ToolboxInterviewSession[]>>(FOGBUGZ_TOOLBOX_INTERVIEW_BASE),
    select: (response) => response.data,
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/** How often a session that awaits Claude is re-read, in case a turn's events were missed. */
export const AWAITING_CLAUDE_REFETCH_MS = 5_000;

/** One session as SpecTree has it now (`GET /:id`). */
export function fetchToolboxInterview(sessionId: string): Promise<DataResponse<ToolboxInterviewSession>> {
  return api.get<DataResponse<ToolboxInterviewSession>>(`${FOGBUGZ_TOOLBOX_INTERVIEW_BASE}/${sessionId}`);
}

/**
 * One session; disabled until there is one. A finished turn invalidates it;
 * while it awaits Claude it is also re-read every few seconds, so a turn
 * whose events the page missed (it finished before the page subscribed)
 * still shows.
 */
export function useToolboxInterview(sessionId: string | null) {
  return useQuery({
    queryKey: fogbugzToolboxInterviewKeys.session(sessionId ?? ""),
    queryFn: () => fetchToolboxInterview(sessionId ?? ""),
    select: (response) => response.data,
    enabled: sessionId !== null,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.data.awaiting === "claude" && query.state.data.data.status === "active"
        ? AWAITING_CLAUDE_REFETCH_MS
        : false,
  });
}

/**
 * Start a session for the routing exactly as chosen and the kind. Refused
 * with 409 `FOGBUGZ_PROJECT_NOT_LINKED` or `REPO_NOT_MAPPED` (details carry
 * the code and the ADO project), creating nothing.
 */
export function useStartToolboxInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: FogbugzFormulatorStartBody) =>
      api.post<DataResponse<ToolboxInterviewSession>>(`${FOGBUGZ_TOOLBOX_INTERVIEW_BASE}/start`, body),
    onSuccess: (response) => {
      queryClient.setQueryData(fogbugzToolboxInterviewKeys.session(response.data.id), response);
      void queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.active() });
    },
  });
}

/**
 * The review step: intent.md, coverage and the Bug bar. Always refetched on
 * mount, so the person reviews what SpecTree would file now.
 */
export function useToolboxInterviewReview(sessionId: string | null) {
  return useQuery({
    queryKey: fogbugzToolboxInterviewKeys.review(sessionId ?? ""),
    queryFn: () =>
      api.get<DataResponse<ToolboxInterviewReview>>(`${FOGBUGZ_TOOLBOX_INTERVIEW_BASE}/${sessionId ?? ""}/review`),
    select: (response) => response.data,
    enabled: sessionId !== null,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
}

/**
 * Save the person's edited intent.md (`PUT /:id/intent`, version-checked).
 * The response carries the re-rendered text, coverage and the re-checked Bug
 * bar; the session and the review are refreshed.
 */
export function useSaveToolboxInterviewIntent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, intentMarkdown, version }: { sessionId: string; intentMarkdown: string; version: number }) =>
      api.put<DataResponse<ToolboxInterviewReview>>(`${FOGBUGZ_TOOLBOX_INTERVIEW_BASE}/${sessionId}/intent`, {
        intentMarkdown,
        version,
      }),
    onSuccess: (response, input) => {
      queryClient.setQueryData(fogbugzToolboxInterviewKeys.review(input.sessionId), response);
      void queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.session(input.sessionId) });
    },
  });
}

/**
 * Possible duplicate open cases for the review step (a warning only, never
 * blocking). A POST, read as a query so it runs once when the step opens.
 */
export function useToolboxInterviewDuplicates(sessionId: string | null) {
  return useQuery({
    queryKey: fogbugzToolboxInterviewKeys.duplicates(sessionId ?? ""),
    queryFn: () =>
      api.post<DataResponse<ToolboxInterviewDuplicates>>(
        `${FOGBUGZ_TOOLBOX_INTERVIEW_BASE}/${sessionId ?? ""}/dedupe`,
        {},
      ),
    select: (response) => response.data,
    enabled: sessionId !== null,
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** The deterministic digest of one `.evtx` file the person added (stateless, no AI). */
export function useToolboxEvtxDigest() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append(TOOLBOX_SUBMIT_FIELDS.digestFile, file, file.name);
      return api.request<DataResponse<ToolboxEvtxDigest>>(`${FOGBUGZ_TOOLBOX_INTERVIEW_BASE}/evtx-digest`, {
        method: "POST",
        body: form,
      });
    },
  });
}

/**
 * File the case: one multipart request with the reviewed intent.md, the
 * reviewed summaries and the files read back from the Toolbox. 400
 * `BELOW_BUG_BAR` (details: the Bug bar result) for a bug case below the
 * bar. On success the session and the resume list are refreshed.
 */
export function useSubmitToolboxInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, ...rest }: SubmitToolboxInterviewInput) =>
      api.request<DataResponse<FogbugzFormulatorSubmitResult>>(`${FOGBUGZ_TOOLBOX_INTERVIEW_BASE}/${sessionId}/submit`, {
        method: "POST",
        body: submitFormData(rest),
      }),
    onSettled: (_response, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.session(input.sessionId) });
      void queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.active() });
    },
  });
}

/**
 * The session's kept files on this computer (`listAttachments`, no bytes).
 * Disabled without the bridge. An `ATTACHMENT_NOT_FOUND`-free empty list is
 * normal on another computer: files stay where they were added.
 */
export function useToolboxInterviewAttachments(
  bridge: FogbugzFormulatorToolsetBridge | null,
  sessionId: string | null,
) {
  return useQuery({
    queryKey: fogbugzToolboxInterviewKeys.attachments(sessionId ?? ""),
    queryFn: async () => {
      if (!bridge || !sessionId) return [];
      const result = await bridge.listAttachments({ sessionId });
      if (!result.ok) throw new Error(result.message);
      return result.attachments;
    },
    enabled: bridge !== null && sessionId !== null,
    retry: false,
  });
}

/** The reviewed `.evtx` summaries a submit carries. */
export type ToolboxReviewedSummaries = FogbugzFormulatorEvtxSummary[];
